import React from 'react';
import PageLayout from '../../components/layout/PageLayout';
import CaseStudyTemplate from '../../components/shared/CaseStudyTemplate';

const DynatechPage = () => (
  <PageLayout>
    <CaseStudyTemplate
      seoTitle="Dynatech Renewables Case Study | Kalobrate"
      seoDescription="See how Kalobrate helped Dynatech Renewables define its values, support recruitment and build culture into leadership, onboarding and technician wellbeing."
      canonical="/results/dynatech-renewables"
      client="Dynatech Renewables"
      headline="Building the right culture before rapid growth"
      sector="Wind energy"
      typeOfWork="Culture by design"
      intro={[
        'Kalobrate began working with Dynatech Renewables when the founders were still building the business.',
        'The company had not yet reached the scale it operates at today, but the founders were already thinking seriously about the culture they wanted to create.',
        'They understood that strong performance in the wind industry would depend on more than technical qualifications. The business needed technicians who would work safely, take responsibility, care about the quality of their work, represent the company well, support colleagues and earn the trust of clients.',
        'The challenge was to define those expectations clearly before growth made the culture harder to shape.',
      ]}
      sections={[
        {
          heading: 'What needed to be understood',
          content: [
            'The founders did not need a set of polished values written for a wall.',
            'They needed to identify the behaviours they genuinely respected and already demonstrated in their own work.',
            'Through leadership conversations, three clear themes emerged:',
            { type: 'list', items: ['Care', 'Trust', 'Pride'] },
            'These values reflected how the founders wanted people to approach their work, clients and colleagues.',
            'They also gave Dynatech Renewables a practical way to think about recruitment. The best technician was not always the person with the longest list of qualifications. Attitude, work ethic, judgement and willingness to go further were also important.',
          ],
        },
        {
          heading: 'What Kalobrate did',
          content: [
            'Kalobrate helped the founders turn those values into practical expectations for leadership, recruitment and onboarding.',
            'The work included:',
            { type: 'list', items: [
              'Helping leaders define what care, trust and pride meant in practice',
              'Connecting values to safety, quality and client service',
              'Exploring how values could guide recruitment decisions',
              'Helping leaders describe the type of technician the business wanted',
              'Delivering wellbeing inductions for new starters',
              'Coaching leaders on how to take part in those sessions authentically',
              'Supporting conversations about mental health in a workforce where stigma remained high',
            ]},
            'The wellbeing inductions were not treated as separate from the wider culture. They gave leaders an early opportunity to show new technicians:',
            { type: 'list', items: [
              'How the business expected people to treat one another',
              'That asking for support was acceptable',
              'That safety included mental as well as physical health',
              'That the stated values applied from the start of employment',
            ]},
          ],
        },
        {
          heading: 'Turning values into a market position',
          content: [
            'As Dynatech Renewables grew, the values became part of how leaders described the organisation and the service it wanted to provide.',
            'A service built with care, delivered with pride and trusted to do the best work.',
            'This gave Dynatech Renewables a clearer point of difference in a crowded market. It connected internal culture to the external promise made to clients.',
            'Care was not only about employee wellbeing. It meant caring about colleagues, safety, clients and the standard of work.',
            'Trust meant being reliable and doing what had been promised.',
            'Pride meant taking ownership of the job and wanting the work to reflect well on the individual and the business.',
          ],
        },
        {
          heading: 'Supporting technicians directly',
          content: [
            'Kalobrate also delivered two-hour mental health workshops for wind turbine technicians.',
            'The sessions were designed around the realities of the workforce, including:',
            { type: 'list', items: [
              'Long shifts',
              'Work away from home',
              'Isolation',
              'Pressure in safety-critical environments',
              'Stigma around mental health',
            ]},
            'The workshops achieved:',
            { type: 'list', items: [
              'Full engagement across the participating groups',
              'An increase in comfort discussing mental health with teammates from 3.9 to 4.1',
              'Stronger trust between leaders and technicians',
            ]},
          ],
        },
        {
          heading: 'What changed',
          content: [
            'The work helped Dynatech Renewables:',
            { type: 'list', items: [
              'Define the behaviours it wanted to build the business around',
              'Communicate those expectations more clearly',
              'Connect culture with recruitment and onboarding',
              'Give leaders a more authentic role in employee inductions',
              'Make conversations about mental health more practical and acceptable',
              'Present a clearer point of difference to clients and prospective technicians',
            ]},
            'Dynatech Renewables has since grown significantly and now supplies technicians to some of the world\'s largest energy companies.',
            'The values work gave its leaders a clear cultural foundation to carry through that growth.',
          ],
        },
      ]}
      quotes={[
        { text: "Without Fred Wellbeing, we wouldn't have been able to build the trust we now have with our staff. The sessions were a turning point in how we talk about mental health.", name: 'Michael Allcock', role: 'Director, Dynatech Renewables' },
        { text: "Ollie made a topic weighed down with stigma feel human and practical. This wasn't just good for our technicians, it was good for us as leaders.", name: 'Kieran Jackson', role: 'Director, Dynatech Renewables' },
      ]}
      mainLesson={[
        'Culture is easier to shape before rapid growth than after it.',
        'Dynatech Renewables did not wait for absence, turnover or low trust to become serious problems. Its founders considered early:',
        'What kind of business they wanted to build. What behaviours they valued. Who they wanted to recruit. How leaders should show those values in practice.',
      ]}
      cta={{
        heading: 'Growing quickly and trying to protect the culture you want to build?',
        copy: 'Kalobrate can help you define what matters, turn it into practical expectations and carry it through recruitment, onboarding and leadership.',
        button: 'Talk to Ollie',
      }}
    />
  </PageLayout>
);

export default DynatechPage;
